<template>
  <div class="menu-page">
    <section class="menu-page__status-grid" aria-label="업무 현황">
      <article v-for="status in statusCards" :key="status.label">
        <span>{{ status.label }}</span>
        <strong>{{ status.value }}</strong>
      </article>
    </section>

    <main class="menu-page__body">
      <section
        v-for="section in menuSections"
        :key="section.title"
        class="menu-section"
      >
        <div class="menu-section__header">
          <div>
            <span class="menu-section__mark">{{ section.mark }}</span>
            <div>
              <strong>{{ section.title }}</strong>
              <p>{{ section.description }}</p>
            </div>
          </div>
          <span>{{ activeCount(section.items) }} / {{ section.items.length }}</span>
        </div>

        <div class="menu-section__grid">
          <NavButton
            v-for="item in section.items"
            :key="item.title"
            :title="item.title"
            :description="item.description"
            :icon="item.icon"
            :color="item.color"
            :link="item.link"
          />
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import NavButton from '@/components/NavButton.vue';
import { computed } from 'vue';

type MenuItem = {
  title: string;
  description: string;
  icon: string;
  color: string;
  link: string;
};

type MenuSection = {
  title: string;
  description: string;
  mark: string;
  items: MenuItem[];
};

const menuSections: MenuSection[] = [
  {
    title: '필수 업무',
    description: '사용자, 환자, 접수, 차트, 청구와 문서 발급',
    mark: '01',
    items: [
      { title: '사용자/권한<br>관리', description: '초대, 목록, 권한 변경', icon: 'USER', color: '#FFF', link: '/users-permissions' },
      { title: '환자 등록<br>기본정보', description: '차트번호, 연락처, 병력', icon: 'PAT', color: '#FFF', link: '/patients' },
      { title: '접수 및<br>내원 관리', description: '대기, 상태, 내원 이력', icon: 'DESK', color: '#FFF', link: '/reception' },
      { title: '전자차트<br>EMR', description: '진료기록, 오더, 이미지', icon: 'EMR', color: '#FFF', link: '/emr' },
      { title: '보험청구<br>관리', description: '청구자료와 오류 확인', icon: 'INS', color: '#FFF', link: '/insurance-claims' },
      { title: '문서 및<br>증명서 발급', description: '영수증, 처방전, 기록부', icon: 'DOC', color: '#FFF', link: '/documents' },
    ],
  },
  {
    title: '진료 운영',
    description: '예약, 상담, 수납, CRM과 오더 관리',
    mark: '02',
    items: [
      { title: '예약 관리<br>캘린더', description: '의료진별 예약 현황', icon: 'CAL', color: '#FFF', link: '/calendar' },
      { title: '예약 입력<br>변경 이력', description: '예약 등록과 상태 변경', icon: 'RSV', color: '#FFF', link: '/reservation' },
      { title: '치료계획<br>상담', description: '상담 자료와 계획 흐름', icon: 'PLAN', color: '#FFF', link: '/plan' },
      { title: '가격 책정<br>치료비 계획', description: '치료 항목별 비용 산정', icon: 'COST', color: '#FFF', link: '/price' },
      { title: '수납 및<br>결제 관리', description: '미수, 환불, 분할수납', icon: 'PAY', color: '#FFF', link: '/payments' },
      { title: '환자관리<br>CRM', description: '리콜, 해피콜, 미이행', icon: 'CRM', color: '#FFF', link: '/client-manage' },
      { title: '문진표<br>PI 입력', description: '문진 항목과 제출', icon: 'FORM', color: '#FFF', link: '/questionnaire' },
      { title: '상담 보드<br>사진', description: '사진 기반 상담 보드', icon: 'IMG', color: '#FFF', link: '/monitor' },
      { title: '오더 및<br>스캔 관리', description: '스캔, 배송, 파일 로그', icon: 'ORD', color: '#FFF', link: '/orders' },
    ],
  },
  {
    title: '관리 및 설정',
    description: '통계, 알림, 마스터와 추후 연동 설정',
    mark: '03',
    items: [
      { title: '통계 및<br>경영관리', description: '매출, 내원, 직원 실적', icon: 'STAT', color: '#FFF', link: '/analytics' },
      { title: '알림 및<br>리콜 기능', description: '알림 규칙과 리콜 관리', icon: 'ALRT', color: '#FFF', link: '/alerts' },
      { title: '코드마스터<br>진료항목', description: '수가, 상병, 행위 코드', icon: 'CODE', color: '#FFF', link: '/codes' },
      { title: '감사 로그<br>이력 조회', description: '사용자 작업 기록', icon: 'LOG', color: '#FFF', link: '/audit' },
    ],
  },
];

const statusCards = computed(() => [
  { label: '전체 메뉴', value: `${allMenuItems.value.length}개` },
  { label: '필수 업무', value: `${menuSections[0].items.length}개` },
  { label: '진료 운영', value: `${menuSections[1].items.length}개` },
  { label: '관리 설정', value: `${menuSections[2].items.length}개` },
]);

const allMenuItems = computed<MenuItem[]>(() => menuSections.flatMap((section) => section.items));

const activeCount = (items: MenuItem[]) => items.filter((item) => item.link).length;
</script>

<style lang="scss">
.menu-page {
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f4f8fa;
  color: #20313c;

  &__hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 270px;
    gap: 18px;
    align-items: stretch;
  }

  &__hero-main,
  &__session,
  &__status-grid article,
  .menu-section {
    border: 1px solid #dbe5e9;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 10px 24px rgba(35, 49, 61, 0.06);
  }

  &__hero-main {
    padding: 22px;
  }

  &__eyebrow {
    margin: 0 0 8px;
    color: #26758d;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0;
  }

  h1 {
    margin: 0;
    color: #172833;
    font-size: 32px;
    font-weight: 900;
    letter-spacing: 0;
  }

  &__summary {
    margin: 8px 0 18px;
    color: #66737d;
    font-size: 14px;
  }

  &__search-wrapper {
    position: relative;
    display: flex;
    max-width: 520px;
  }

  &__search-input {
    width: 100%;
    height: 42px;
    padding: 0 40px 0 13px;
    border-radius: 8px;
    border: 1px solid #cfdce2;
    background: #fff;
    color: #20313c;
    font-size: 15px;
    box-sizing: border-box;
  }

  &__icon-search {
    position: absolute;
    right: 12px;
    top: 50%;
    width: 18px;
    height: 18px;
    transform: translateY(-50%);
    cursor: pointer;
  }

  &__suggestions {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 20;
    width: 100%;
    max-height: 360px;
    overflow-y: auto;
    border: 1px solid #cfdce2;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 18px 40px rgba(35, 49, 61, 0.16);
    padding: 6px;
    box-sizing: border-box;

    button {
      display: grid;
      grid-template-columns: 42px minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 58px;
      border: 0;
      border-radius: 8px;
      background: #fff;
      color: #20313c;
      text-align: left;
      cursor: pointer;

      &:hover {
        background: #eef7f9;
      }

      &.disabled {
        color: #8b949b;
        cursor: not-allowed;

        .suggestion-icon {
          background: #edf0f2;
          color: #8b949b;
        }
      }
    }

    strong {
      display: block;
      font-size: 13px;
      line-height: 1.25;
    }

    small {
      display: block;
      margin-top: 4px;
      color: #6b7a83;
      font-size: 12px;
      line-height: 1.3;
    }

    em {
      padding: 3px 7px;
      border-radius: 999px;
      background: #eaf3f6;
      color: #2a7086;
      font-size: 11px;
      font-style: normal;
      font-weight: 900;
    }
  }

  &__empty-search {
    padding: 18px;
    color: #8a969d;
    text-align: center;
    font-size: 13px;
  }

  &__session {
    padding: 18px;
    display: grid;
    align-content: space-between;
    gap: 16px;
  }

  &__logo {
    width: 103px;
    height: 34px;
  }

  &__session strong,
  &__session span {
    display: block;
  }

  &__session strong {
    font-size: 17px;
    color: #172833;
  }

  &__session span {
    margin-top: 4px;
    color: #66737d;
    font-size: 13px;
  }

  &__logout {
    height: 36px;
    border: 1px solid #cfdce2;
    border-radius: 8px;
    background: #fff;
    color: #52646f;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  &__status-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  &__status-grid article {
    padding: 14px;
    display: grid;
    gap: 6px;
  }

  &__status-grid span {
    color: #66737d;
    font-size: 13px;
    font-weight: 700;
  }

  &__status-grid strong {
    color: #172833;
    font-size: 22px;
  }

  &__body {
    display: grid;
    grid-template-columns: .82fr 1.18fr 1.22fr;
    align-items: start;
    gap: 16px;
  }
}

.suggestion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #eaf3f6;
  color: #1d6f86;
  font-size: 11px;
  font-weight: 900;
}

.menu-section {
  height: fit-content;
  padding: 16px;
  box-sizing: border-box;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;

    > div {
      display: flex;
      gap: 10px;
      min-width: 0;
    }

    strong {
      color: #344954;
      font-size: 16px;
      font-weight: 900;
    }

    p {
      margin: 4px 0 0;
      color: #74838b;
      font-size: 12px;
      line-height: 1.35;
    }

    > span {
      flex-shrink: 0;
      padding: 4px 8px;
      border-radius: 999px;
      background: #eaf3f6;
      color: #2a7086;
      font-size: 12px;
      font-weight: 900;
    }
  }

  &__mark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #1d6f86;
    color: #fff;
    font-size: 12px;
    font-weight: 900;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
  }
}

@media (max-width: 1200px) {
  .menu-page {
    &__hero,
    &__body {
      grid-template-columns: 1fr;
    }

    &__status-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .menu-section {
    max-height: none;

    &__grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

@media (max-width: 720px) {
  .menu-page {
    min-height: auto;
    padding: 12px;
    gap: 12px;

    &__status-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    &__status-grid article {
      padding: 10px 12px;
    }

    &__status-grid span {
      font-size: 12px;
    }

    &__status-grid strong {
      font-size: 18px;
    }

    &__body {
      gap: 12px;
    }
  }

  .menu-section {
    padding: 12px;

    &__header {
      margin-bottom: 10px;
    }

    &__header strong {
      font-size: 15px;
    }

    &__header p {
      display: none;
    }

    &__mark {
      width: 30px;
      height: 30px;
    }
  }

  .menu-section__grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

@media (max-width: 390px) {
  .menu-page {
    &__status-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
